'use client';

import { ProductInterface, ProductVariantInterface } from '@qlevr/shared/interfaces';
import { AttributeInputShopify } from '@qlevr/shared/schema';
import React, { Suspense, createContext, useContext, useState } from 'react';

interface ProductProviderProps {
  children: React.ReactNode;
  product?: ProductInterface | undefined;
}

interface ProductContextType {
  product: ProductInterface | undefined;
  attributes: AttributeInputShopify[];
  setAttributes: React.Dispatch<React.SetStateAction<AttributeInputShopify[]>>;
  selectedVariant: ProductVariantInterface | null;
  setSelectedVariant: React.Dispatch<React.SetStateAction<ProductVariantInterface | null>>;
}

export const ProductContext = createContext<ProductContextType>({
  product: undefined,
  attributes: [],
  setAttributes: () => {},
  selectedVariant: null,
  setSelectedVariant: () => {},
});

const ProductProviderComponent: React.FC<ProductProviderProps> = ({ children, product }) => {
  const [attributes, setAttributes] = React.useState<AttributeInputShopify[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariantInterface | null>(null);

  return (
    <ProductContext.Provider
      value={{
        product,
        attributes,
        setAttributes,
        selectedVariant,
        setSelectedVariant,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const ProductProvider: React.FC<ProductProviderProps> = ({ children, product }) => {
  return (
    <Suspense>
      <ProductProviderComponent product={product}>{children}</ProductProviderComponent>
    </Suspense>
  );
};

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};
